import { ReactNode, useEffect, useState } from "react"
import { useSession } from "../../auth/session"
import Api from "../../api/api"
import { Organization } from "../../api/models"
import { buildImageSrcSet } from "./imageSrcSet"

// The org's logo at chip size — the OrgChip rendering, exported for the
// org ChipSelect filters (Skus, Products). Orgs without a logo get no icon
// and the row shows just the label.
export function orgLogoIcon(org: Organization): ReactNode {
  if (!org.imageUrl) return undefined
  return (
    <img
      src={org.imageUrl}
      srcSet={buildImageSrcSet(org.imageUrl, org.availableWidths ?? undefined)}
      sizes="16px"
      alt=""
      style={{ width: 16, height: 16, objectFit: "contain", borderRadius: 2 }}
    />
  )
}

// Organization attribution on admin listing cards: the org's logo (when one
// is set) and display name, with its handle as the tooltip. Records come
// from GET /admin/organizations, fetched once per session (module-level
// promise cache — org rosters are tiny and rename-rare); unknown ids
// degrade to the raw handle. UserChip is the widget for genuine USER
// attributions (e.g. who minted a code batch) — this one is for org-owned
// rows (sku.ownerId is an org id).
let orgsPromise: Promise<Map<string, Organization>> | null = null

function orgs(accessToken: string): Promise<Map<string, Organization>> {
  if (orgsPromise === null)
    orgsPromise = Api.listOrganizations({ accessToken })
      .then(({ organizations }) => new Map(organizations.map(o => [o.id, o])))
      .catch(() => {
        orgsPromise = null   // allow a retry on the next mount
        return new Map<string, Organization>()
      })
  return orgsPromise
}

// One org record off the same session cache — undefined while loading (or
// without an id yet), null when the id is unknown. The SKU page reads the
// owner org's iLok switch through it.
export function useOrganization(orgId: string | undefined): Organization | null | undefined {
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const [org, setOrg] = useState<Organization | null | undefined>(undefined)

  useEffect(() => {
    if (!accessToken || orgId === undefined) return
    let stale = false
    orgs(accessToken).then(records => {
      if (!stale) setOrg(records.get(orgId) ?? null)
    })
    return () => { stale = true }
  }, [orgId, accessToken])

  return org
}

export default function OrgChip({ orgId }: { orgId: string }) {
  const org = useOrganization(orgId) ?? null

  return (
    <span className="product-owner product-owner-static" title={orgId}>
      {org?.imageUrl && (
        <img
          src={org.imageUrl}
          srcSet={buildImageSrcSet(org.imageUrl, org.availableWidths ?? undefined)}
          sizes="16px"
          alt=""
          style={{ width: 16, height: 16, objectFit: "contain", borderRadius: 2 }}
        />
      )}
      <span className="product-owner-id">{org?.name ?? orgId}</span>
    </span>
  )
}
